import { CircularProgress, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import { forwardRef, useEffect } from "react";
import { useState } from "react";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  SafetyDashboardApiFactory,
  DriverHarshEventsBreakdownDto,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { Widget } from "../../../assets/maintenance-dashboard/widgets/Widget";
import { DriverWidgetProps } from "../common";
import styles from "./DriverHarshEventsBreakdown.module.css";

const useStyles = makeStyles((theme: Theme) => ({
  red: {
    color: theme.palette.error.main,
    cursor: "pointer",
    "&:hover": {
      textShadow: `0px 0px 2px ${theme.palette.error.main}, 0 0 1em rgba(255, 255, 255, 0.25), 0 0 0.2em rgba(255, 255, 255, 0.5)`,
    },
  },
  yellow: {
    color: theme.palette.warning.main,
    cursor: "pointer",
    "&:hover": {
      textShadow: `0px 0px 2px ${theme.palette.warning.main}, 0 0 1em rgba(255, 255, 255, 0.25), 0 0 0.2em rgba(255, 255, 255, 0.5)`,
    },
  },
}));

export const DriverHarshEventsBreakdown = forwardRef(
  ({ className, icon, title, driver, ...rest }: DriverWidgetProps, ref) => {
    const classes = useStyles();
    const [harshEvents, setHarshEvents] = useState<
      DriverHarshEventsBreakdownDto
    >();
    const [loading, setLoading] = useState<boolean>(true);
    const axios = useAxiosInstance();
    useEffect(() => {
      if (!axios) {
        return;
      }

      if (!driver?.id) {
        setLoading(false);
        return;
      }

      setLoading((x) => true);
      SafetyDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppSafetydashboardIdHarsheventsbreakdownfordriverGet(driver.id)
        .then((result) => {
          setHarshEvents(result.data);
        })
        .catch((e) => console.error(handleAxiosError(e)))
        .finally(() => setLoading(false));
    }, [axios, driver.id]);

    const {
      harshAccelerationEvents,
      harshBrakeEvents,
      harshStopEvents,
      harshTurnEvents,
      speedingEvents,
      physicalEldDisconnectionEvents,
    } = harshEvents
      ? harshEvents
      : {
          harshAccelerationEvents: 0,
          harshBrakeEvents: 0,
          harshStopEvents: 0,
          harshTurnEvents: 0,
          speedingEvents: 0,
          physicalEldDisconnectionEvents: 0,
        };
    return (
      <Widget
        title={title}
        icon={icon}
        className={className}
        ref={ref}
        {...rest}
      >
        <div className={styles["categoriesWrapper"]}>
          {loading ? (
            <div className={styles.categories}>
              <div className={classNames(styles.category, "category")}>
                <CircularProgress size="3rem" />
              </div>
            </div>
          ) : (
            <>
              <div className={styles.categories}>
                <div className={styles.category}>
                  <div
                    className={`${styles["category-value"]} ${classes.yellow}`}
                  >
                    {harshAccelerationEvents}
                  </div>
                  <div className={styles["category-title"]}>
                    Harsh acceleration
                  </div>
                </div>
                <div className={styles.category}>
                  <div className={`${styles["category-value"]} ${classes.red}`}>
                    {harshBrakeEvents}
                  </div>
                  <div className={styles["category-title"]}>Harsh braking</div>
                </div>
              </div>
              <div className={styles.categories}>
                <div className={styles.category}>
                  <div
                    className={`${styles["category-value"]} ${classes.yellow}`}
                  >
                    {harshTurnEvents}
                  </div>
                  <div className={styles["category-title"]}>
                    Taking harsh turn
                  </div>
                </div>
                <div className={styles.category}>
                  <div className={`${styles["category-value"]} ${classes.red}`}>
                    {harshStopEvents}
                  </div>
                  <div className={styles["category-title"]}>
                    Possible impact
                  </div>
                </div>
              </div>
              <div className={styles.categories}>
                <div className={styles.category}>
                  <div className={`${styles["category-value"]} ${classes.red}`}>
                    {speedingEvents}
                  </div>
                  <div className={styles["category-title"]}>Speeding</div>
                </div>
                <div className={styles.category}>
                  <div className={`${styles["category-value"]} ${classes.red}`}>
                    {physicalEldDisconnectionEvents}
                  </div>
                  <div className={styles["category-title"]}>
                    Removing ELD device
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Widget>
    );
  }
);
